import { ReactNode } from 'react';
import { useContentArtifactsGate, useNavigationArtifactsGate } from '@/enterprise/data/useArtifacts';
import FullPageLoader from '@/shared/components/FullPageLoader';

/**
 * Blocks first paint until the artifacts requests settle, so artifact-driven nav items
 * (Fundbox, Extended Network Finance) and the operational alert are present on the first
 * render instead of popping in after the app has painted.
 *
 * Both requests start here and run in parallel, so the wait is the slower of the two rather
 * than the sum. Because the content slice is a shared cache, MARKETING_MESSAGE and ALERT
 * consumers (MarketingWidget, AppLayout) read it warm and never show a loading state.
 *
 * Must be rendered below `FeaturesProvider` (so the `enterprise-ui_useMarketingArtifacts`
 * flag is resolved) and above the navigation and banner consumers.
 *
 * Behavior:
 * - Flag off → both queries are disabled, `isLoading` is false, children render immediately
 *   (no artifact-driven nav items, no alert — current behavior).
 * - Flag on + either request in flight → render the full-page loader.
 * - Flag on + both settled (success or error) → render children with warm caches, so
 *   `useNavigationItems` and `useAlertArtifacts` read data synchronously on first paint.
 *
 * Errors are non-fatal: the queries use `throwOnError: false`, so a failed request leaves
 * `isLoading` false and the app paints normally (nav without those items, no alert).
 */
const ArtifactsGate = ({ children }: { children: ReactNode }) => {
  const { isLoading: isNavigationLoading } = useNavigationArtifactsGate();
  const { isLoading: isContentLoading } = useContentArtifactsGate();

  if (isNavigationLoading || isContentLoading) {
    return <FullPageLoader />;
  }

  return <>{children}</>;
};

export default ArtifactsGate;
